import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, Network, Search } from '@/components/icons';
import { Input } from '@/components/ui/input';
import EntityBadge from '@/components/haccp/EntityBadge';
import StatusBadge from '@/components/haccp/StatusBadge';
import ProductThumbnail from '@/components/haccp/ProductThumbnail';
import { ErrorState, LoadingState } from '@/components/haccp/AsyncState';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const dateOnly = value => value ? new Date(value).toLocaleDateString('it-IT') : '—';
const lotName = lot => lot?.commercial_name || lot?.ingredient_name || 'Prodotto';
const numberFormat = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 3 });

const movementLabels = {
  load: 'Carico',
  production_output: 'Produzione / carico lotto',
  adjustment: 'Rettifica',
  waste: 'Scarto',
  sale: 'Vendita',
  production_use: 'Consumo produzione',
  use: 'Utilizzo',
  other: 'Movimento',
  unknown: 'Movimento non disponibile',
};

const movementLabel = kind => movementLabels[kind] || movementLabels.other;
const formatNumber = value => {
  const number = Number(value);
  return Number.isFinite(number) ? numberFormat.format(number) : '—';
};
const formatSignedQuantity = (value, unit = '') => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '—';
  const sign = number > 0 ? '+' : number < 0 ? '−' : '';
  return `${sign}${numberFormat.format(Math.abs(number))}${unit ? ` ${unit}` : ''}`;
};

export default function TraceabilityPanel({ initialLotId = '', initialQuery = '', onLotChange }) {
  const [lots, setLots] = useState([]);
  const [query, setQuery] = useState(initialQuery || '');
  const [selectedLotId, setSelectedLotId] = useState(initialLotId || '');
  const [trace, setTrace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/lots/`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error('Impossibile caricare i lotti')))
      .then(setLots)
      .catch(e => setError(e.message));
  }, []);

  useEffect(() => { if (initialLotId) setSelectedLotId(initialLotId); }, [initialLotId]);
  useEffect(() => { if (initialQuery) setQuery(initialQuery); }, [initialQuery]);

  useEffect(() => {
    if (!selectedLotId) { setTrace(null); return; }
    let cancelled = false;
    setLoading(true); setError('');
    fetch(`${BACKEND_URL}/api/traceability/${encodeURIComponent(selectedLotId)}?direction=complete`, { credentials: 'include' })
      .then(async r => { if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Impossibile ricostruire la tracciabilità'); } return r.json(); })
      .then(data => { if (!cancelled) setTrace(data); })
      .catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedLotId]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return lots.filter(l => [l.lot_number, l.commercial_name, l.ingredient_name, l.product_code].some(v => String(v || '').toLowerCase().includes(q))).slice(0, 8);
  }, [lots, query]);

  const selectLot = lot => {
    setSelectedLotId(lot.lot_id);
    onLotChange?.(lot.lot_id);
    setQuery(`${lot.lot_number} · ${lot.commercial_name || lot.ingredient_name || 'Prodotto'}`);
  };
  const root = trace?.root_lot;
  const backward = trace?.backward_edges || [];
  const forward = trace?.forward_edges || [];
  const events = trace?.events || [];

  return <div className="space-y-3">
    <div className="surface px-3 py-3 sm:px-4 space-y-2">
      <label className="text-xs sm:text-sm font-semibold text-[#344054]">Cerca lotto, ingrediente o prodotto</label>
      <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"/><Input className="h-10 pl-9" value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca lotto, ingrediente o prodotto"/></div>
      {matches.length > 0 && <div className="border rounded-lg divide-y overflow-hidden">{matches.map(l => <button type="button" key={l.lot_id} onClick={() => selectLot(l)} className="w-full min-h-12 p-2.5 text-left hover:bg-slate-50 flex items-center gap-3"><ProductThumbnail item={l} size="table"/><span className="min-w-0 flex-1"><span className="block text-sm font-medium truncate">{l.commercial_name || l.ingredient_name}</span><span className="text-xs text-muted-foreground">Lotto {l.lot_number}</span></span><EntityBadge entity="lot" label={l.lot_number} compact/></button>)}</div>}
    </div>

    {loading ? <LoadingState/> : error ? <ErrorState description={error}/> : !root ? <div className="surface p-6 text-center"><Network className="w-9 h-9 mx-auto text-primary/50"/><p className="font-medium mt-2">Seleziona un lotto</p><p className="text-sm text-muted-foreground mt-1">Visualizza provenienza, produzioni e prodotti derivati.</p></div> : <>
      <div className="surface p-3 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <ProductThumbnail item={root} size="table"/>
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Lotto analizzato</p>
              <h2 className="font-heading text-lg font-medium leading-tight mt-0.5 truncate">{lotName(root)}</h2>
              <div className="mt-1.5"><EntityBadge entity="lot" label={root.lot_number} compact/></div>
            </div>
          </div>
          <div className="text-xs sm:text-sm text-right shrink-0">
            <p><span className="font-medium">{root.initial_quantity} {root.unit}</span> iniziali</p>
            <p className="text-muted-foreground mt-0.5">Scadenza {dateOnly(root.expiry_date)}</p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 mt-3"><Metric label="Fornitore / origine" value={root.supplier_name || '—'}/><Metric label="DDT / riferimento" value={root.document_ref || '—'}/><Metric label="Esito ricevimento" node={root.origin === 'batch' ? <span>Non applicabile · produzione interna</span> : <div className="space-y-0.5"><StatusBadge status={root.receipt_status}/>{root.receipt_status === 'non_compliant' && root.receipt_reason ? <p className="text-[11px] text-red-700">{root.receipt_reason}</p> : null}</div>}/><Metric label="Residuo" value={`${root.current_quantity ?? '—'} ${root.unit || ''}`}/></div>
        {trace?.lot_outcome?.status === 'depleted' && <LotOutcome outcome={trace.lot_outcome}/>} 
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <TraceColumn title="Origine" subtitle="Materie prime e lotti utilizzati" edges={backward}/>
        <TraceColumn title="Destinazione" subtitle="Produzioni e lotti generati" edges={forward}/>
      </div>
      <MovementEvents events={events} truncated={trace?.events_truncated}/>
      {trace?.truncated && <p className="text-xs text-amber-700">Catena limitata per dimensione: sono mostrati i collegamenti entro i limiti di sicurezza del motore.</p>}
    </>}
  </div>;
}

function LotOutcome({ outcome }) {
  const knownKind = outcome?.kind && outcome.kind !== 'unknown';
  const quantity = outcome?.quantity != null ? `${formatNumber(outcome.quantity)}${outcome.unit ? ` ${outcome.unit}` : ''}` : '';
  const details = knownKind
    ? [`Movimento di esaurimento: ${movementLabel(outcome.kind)}`, quantity, outcome.created_at ? dateOnly(outcome.created_at) : ''].filter(Boolean).join(' · ')
    : 'Il lotto risulta esaurito; il movimento che ha portato il residuo a zero non è disponibile nello storico.';
  return <div className="mt-2 border-t border-slate-100 pt-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
    <div className="flex items-center gap-2 shrink-0"><span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Esito lotto</span><span className="text-sm font-medium">Esaurito</span></div>
    <p className="text-xs text-muted-foreground sm:text-right">{details}</p>
  </div>;
}

function MovementEvents({ events, truncated }) {
  return <div className="surface p-3 sm:p-4">
    <div className="flex items-start justify-between gap-3 mb-2">
      <div><h3 className="font-heading font-medium">Eventi del lotto</h3><p className="text-xs text-muted-foreground">Movimenti di magazzino separati dalla genealogia di produzione</p></div>
      {events.length > 0 && <span className="text-xs text-muted-foreground shrink-0">{events.length} movimenti</span>}
    </div>
    {!events.length ? <p className="text-sm text-muted-foreground py-2.5">Nessun movimento registrato.</p> : <div className="divide-y divide-slate-100">{events.map((event, index) => <MovementEvent key={event.movement_id || `${event.created_at}-${index}`} event={event}/>)}</div>}
    {truncated && <p className="text-xs text-amber-700 mt-2">Sono mostrati i movimenti più recenti entro il limite di sicurezza.</p>}
  </div>;
}

function MovementEvent({ event }) {
  const unit = event.unit || '';
  const detail = `${formatNumber(event.quantity_before)} → ${formatNumber(event.quantity_after)}${unit ? ` ${unit}` : ''}`;
  const labelClass = event.kind === 'waste' ? 'text-red-700' : 'text-slate-900';
  return <div className="py-2.5 first:pt-1 last:pb-1 grid gap-1 sm:grid-cols-[110px_minmax(0,1fr)_150px] sm:gap-4 sm:items-center">
    <p className="text-xs text-muted-foreground">{dateOnly(event.created_at)}</p>
    <div className="min-w-0"><p className={`text-sm font-medium ${labelClass}`}>{movementLabel(event.kind)}</p><p className="text-xs text-muted-foreground truncate sm:whitespace-normal">{event.notes || '—'}</p></div>
    <div className="sm:text-right"><p className={`text-sm font-semibold ${labelClass}`}>{formatSignedQuantity(event.quantity_change, unit)}</p><p className="text-xs text-muted-foreground">{detail}</p></div>
  </div>;
}

function TraceColumn({ title, subtitle, edges }) {
  return <div className="surface p-3 sm:p-4"><div className="mb-2"><h3 className="font-heading font-medium">{title}</h3><p className="text-xs text-muted-foreground">{subtitle}</p></div>{!edges.length ? <p className="text-sm text-muted-foreground py-2.5">Nessun collegamento registrato.</p> : <div className="space-y-2">{edges.map((edge, index) => {
    const source = edge.from_lot; const target = edge.to_lot; const batch = edge.batch || {};
    return <div key={`${batch.batch_id}-${source?.lot_id}-${target?.lot_id}-${index}`} className="rounded-lg border p-2.5">
      <LotLine lot={source}/><div className="my-2 pl-3 border-l-2 border-slate-200 space-y-1"><ArrowDown className="w-4 h-4 text-muted-foreground"/><p className="text-sm font-medium">Produzione {batch.batch_id || '—'}</p><p className="text-xs text-muted-foreground">{batch.product || 'Produzione'} · {dateOnly(batch.production_date)}{batch.quantity_used != null ? ` · usati ${batch.quantity_used} ${batch.unit || ''}` : ''}</p><ArrowDown className="w-4 h-4 text-muted-foreground"/></div><LotLine lot={target}/>
    </div>;
  })}</div>}</div>;
}

function LotLine({ lot }) { return <div className="flex items-center gap-2.5"><ProductThumbnail item={lot} size="table"/><div className="min-w-0"><p className="text-sm font-medium truncate">{lotName(lot)}</p><p className="text-xs text-muted-foreground truncate">Lotto {lot?.lot_number || '—'}{lot?.supplier_name ? ` · ${lot.supplier_name}` : ''}</p></div></div>; }
function Metric({ label, value, node }) { return <div className="surface-muted px-3 py-2.5"><p className="text-[11px] text-muted-foreground">{label}</p><div className="text-sm font-medium mt-0.5 break-words">{node || value}</div></div>; }
